import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Users,
  Search,
  Calendar,
  Package,
  MessageCircle,
  Phone,
  Mail,
  ChevronRight,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { useTranslation } from "react-i18next";

export default function MyStudentsModule({ user, businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [trainerId, setTrainerId] = useState(null);
  const [currentBusinessId, setCurrentBusinessId] = useState(businessId);
  const [businessName, setBusinessName] = useState("");

  useEffect(() => {
    if (user?.user_id) {
      fetchMyStudents();
    }
  }, [user, businessId]);

  const fetchMyStudents = async () => {
    try {
      // First try to get the selected trainer business from localStorage
      const savedIndex = localStorage.getItem('selectedTrainerBusinessIndex');
      let bizId = businessId;
      let currentTrainerId = null;
      
      // Get trainer's businesses
      const trainerBizRes = await fetch(`${API}/me/trainer-businesses`, {
        credentials: "include",
      });
      
      if (trainerBizRes.ok) {
        const trainerBusinesses = await trainerBizRes.json();
        if (trainerBusinesses.length > 0) {
          // Use the selected business index from localStorage
          const selectedIdx = savedIndex ? parseInt(savedIndex, 10) : 0;
          const selectedBusiness = trainerBusinesses[selectedIdx] || trainerBusinesses[0];
          bizId = businessId || selectedBusiness.business_id;
          setCurrentBusinessId(bizId);
          setBusinessName(selectedBusiness.name || "");
        }
      }
      
      // Also get trainer info for trainer_id
      const trainerRes = await fetch(`${API}/trainers/by-user/${user.user_id}`, {
        credentials: "include",
      });
      
      if (trainerRes.ok) {
        const trainers = await trainerRes.json();
        // Find the trainer record for the current business
        const currentTrainer = trainers.find(t => t.business_id === bizId) || trainers[0];
        if (currentTrainer) {
          currentTrainerId = currentTrainer.trainer_id;
          setTrainerId(currentTrainerId);
        }
      }
      
      if (bizId && currentTrainerId) {
        // Get ONLY trainer's students (members who had lessons with this trainer or packages assigned)
        const studentsRes = await fetch(
          `${API}/trainers/${currentTrainerId}/my-students`,
          { credentials: "include" }
        );
        
        if (studentsRes.ok) {
          const allMembers = await studentsRes.json();
          
          // Enrich with package info
          const enrichedMembers = await Promise.all(
            allMembers.map(async (member) => {
              try {
                const pkgRes = await fetch(
                  `${API}/members/${member.member_id}/packages`,
                  { credentials: "include" }
                );
                if (pkgRes.ok) {
                  const packages = await pkgRes.json();
                  const activePackages = packages.filter(
                    (p) => p.status !== "cancelled" && p.is_active !== false && p.remaining_sessions > 0
                  );
                  return {
                    ...member,
                    packages: activePackages,
                    total_remaining_sessions: activePackages.reduce((sum, p) => sum + (p.remaining_sessions || 0), 0),
                  };
                }
              } catch (e) {
                console.error("Error fetching packages for member:", member.member_id);
              }
              return { ...member, packages: [], total_remaining_sessions: 0 };
            })
          );
          
          setStudents(enrichedMembers);
        }
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(
    (s) =>
      s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.surname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.member_no?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  // Show message if no business is selected
  if (!currentBusinessId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-grey">
        <Users className="w-12 h-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">{t("common:i̇sletmeSecilmedi")}</p>
        <p className="text-sm">{t("common:solMenudenBirIsletmeSecin")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('members:allMembers')}</h2>
          <p className="text-grey text-sm">
            {businessName ? `${businessName} - ` : ""}{students.length} üye ile ders yapabilirsiniz
          </p>
        </div>
        
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
          <Input
            placeholder={t('members:searchMember')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 rounded-xl"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{students.length}</p>
                <p className="text-xs text-grey">{t("common:toplamUye")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">
                  {students.filter((s) => s.packages?.length > 0).length}
                </p>
                <p className="text-xs text-grey">Aktif Paketli</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">
                  {students.reduce((sum, s) => sum + (s.total_remaining_sessions || 0), 0)}
                </p>
                <p className="text-xs text-grey">Toplam Kalan Ders</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Students List */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">{t('members:memberList')}</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredStudents.length > 0 ? (
            <div className="space-y-3">
              {filteredStudents.map((student) => (
                <div
                  key={student.member_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid={`member-card-${student.member_id}`}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-navy text-white">
                        {student.name?.charAt(0)}{student.surname?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-navy">
                          {student.name} {student.surname}
                        </p>
                        {student.packages?.length > 0 ? (
                          <Badge className="bg-green-100 text-green-600">
                            {student.total_remaining_sessions} ders kaldı
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-grey">Paket yok</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-grey mt-1">
                        <span>#{student.member_no}</span>
                        {student.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {student.phone}
                          </span>
                        )}
                      </div>
                      {/* Show packages */}
                      {student.packages?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {student.packages.slice(0, 2).map((pkg) => (
                            <Badge key={pkg.member_package_id} variant="secondary" className="text-xs">
                              {pkg.package_name}: {pkg.remaining_sessions} ders
                            </Badge>
                          ))}
                          {student.packages.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{student.packages.length - 2} paket
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-navy">{student.remaining_sessions || 0}</p>
                      <p className="text-xs text-grey">{t("members:remainingSessions")}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-grey" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>
                {searchQuery
                  ? t("common:noMatchingStudents")
                  : t("common:noStudents")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
